import { memo } from "react";
import { cn } from "@/lib/utils";

interface ContadorCaracteresProps {
  current: number;
  min?: number;
  max?: number;
  className?: string;
  showWarning?: boolean;
}

/**
 * ContadorCaracteres - Contador visual de caracteres para campos de texto
 * 
 * Muestra el progreso hacia mínimo/máximo con colores indicativos:
 * - Rojo: Por debajo del mínimo
 * - Ámbar: Cerca del límite máximo (>80%)
 * - Verde: Dentro del rango óptimo
 * 
 * @example
 * <ContadorCaracteres current={text.length} min={50} max={500} />
 */
export const ContadorCaracteres = memo(({
  current,
  min = 0,
  max,
  className,
  showWarning = true,
}: ContadorCaracteresProps) => {
  const isBelowMin = min > 0 && current < min;
  const isNearMax = max && current >= max * 0.8;
  const isAtMax = max && current >= max;
  const isOptimal = !isBelowMin && !isAtMax;

  // Calculate percentage for progress bar if max is set
  const percentage = max ? Math.min((current / max) * 100, 100) : 0;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Progress bar (only if max is set) */}
      {max && (
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[100px]">
          <div
            className={cn(
              "h-full transition-all duration-300 rounded-full",
              isBelowMin && "bg-destructive",
              isNearMax && !isAtMax && "bg-amber-500",
              isAtMax && "bg-destructive",
              isOptimal && "bg-green-500"
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}

      {/* Character count */}
      <span
        className={cn(
          "text-xs font-medium tabular-nums transition-colors",
          isBelowMin && "text-destructive",
          isNearMax && !isAtMax && "text-amber-600 dark:text-amber-400",
          isAtMax && "text-destructive",
          isOptimal && "text-muted-foreground"
        )}
      >
        {current}
        {max && `/${max}`}
        {min > 0 && current < min && (
          <span className="ml-1">(mín. {min})</span>
        )}
      </span>

      {/* Warning messages */}
      {showWarning && (
        <>
          {isBelowMin && (
            <span className="text-xs text-destructive">
              Faltan {min - current} caracteres
            </span>
          )}
          {isAtMax && (
            <span className="text-xs text-destructive">
              Límite alcanzado
            </span>
          )}
        </>
      )}
    </div>
  );
});

ContadorCaracteres.displayName = "ContadorCaracteres";
